import React, { useState } from "react"
import { Button, Flex, Dropdown, Tooltip, Card } from "antd"
import { MoreOutlined, InfoCircleOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import DataTable from "ui/DataTable"
import CustomPagination from "components/common/CustomPagination"
import { ConfirmModal } from "ui/ConfirmModal"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import BookingEmptyImage from "ui/Icon/DSIcon/BookingEmptyImage"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import ScheduleTimeOffModal from "./modal/ScheduleTimeOff"
import styles from "./index.module.css"

const ScheduledTimeOff = ({ timeOffData = [] }) => {
  const { t } = useTranslation()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [recordToDelete, setRecordToDelete] = useState(null)

  const handleAdd = () => {
    setEditingRecord(null)
    setIsModalOpen(true)
  }

  const handleEdit = (record) => {
    setEditingRecord(record)
    setIsModalOpen(true)
  }

  const handleDelete = (record) => {
    setRecordToDelete(record)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = () => {
    if (recordToDelete) {
      // TODO: Implement actual delete API call
      console.log("Delete time off", recordToDelete)
    }
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRecordToDelete(null)
  }

  const handleSave = (values) => {
    // TODO: Implement save logic
    console.log("Save time off", values)
    setIsModalOpen(false)
  }

  const handleSaveAndAddNew = (values) => {
    // TODO: Implement save and add new logic
    console.log("Save and add new time off", values)
    // Keep modal open for new entry
  }

  // Calculate pagination
  const total = timeOffData.length
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedData = timeOffData.slice(startIndex, endIndex)

  const metadata = {
    page: currentPage,
    total_pages: Math.ceil(total / pageSize),
    next_page: currentPage < Math.ceil(total / pageSize) ? currentPage + 1 : null,
    prev_page: currentPage > 1 ? currentPage - 1 : null
  }

  // Define table columns
  const columns = [
    {
      title: t("crewMember.scheduledTimeOff.table.columns.day"),
      dataIndex: "day",
      key: "day",
      sorter: (a, b) => a.day.localeCompare(b.day)
    },
    {
      title: t("crewMember.scheduledTimeOff.table.columns.dates"),
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date)
    },
    {
      title: t("crewMember.scheduledTimeOff.table.columns.time"),
      dataIndex: "time",
      key: "time",
      sorter: (a, b) => a.time.localeCompare(b.time),
      render: (time, record) => (
        <Flex align="center" gap={8}>
          <span>{time}</span>
          {record.hasInfo && record.tooltipContent && (
            <Tooltip
              title={
                <span className={styles.tooltipContent}>
                  <strong>{record.day}</strong> {record.tooltipContent}
                </span>
              }
              overlayClassName={styles.customTooltip}
              placement="top"
            >
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          )}
        </Flex>
      )
    },
    {
      title: t("crewMember.scheduledTimeOff.table.columns.actions"),
      key: "actions",
      align: "right",
      width: 80,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "edit",
                icon: <EditOutlined />,
                label: t("crewMember.scheduledTimeOff.table.actions.edit"),
                onClick: () => handleEdit(record)
              },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: t("crewMember.scheduledTimeOff.table.actions.delete"),
                danger: true,
                onClick: () => handleDelete(record)
              }
            ]
          }}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} className={styles.moreButton} />
        </Dropdown>
      )
    }
  ]

  return (
    <>
      <Flex vertical gap={16} className={styles.container}>
        {/* Header */}
        <Flex align="center" justify="space-between" className={styles.header}>
          <h2 className={styles.title}>{t("crewMember.scheduledTimeOff.table.title")}</h2>
          <Button type="link" onClick={handleAdd} className={styles.addButton}>
            {t("crewMember.scheduledTimeOff.buttons.add")}
          </Button>
        </Flex>

        {/* Table */}
        <WithEmptyStateWrapper
          name="time off"
          hasItems={timeOffData.length > 0}
          fallback={
            <Card className="py-5xl">
              <Flex vertical justify="center" align="center" gap={12}>
                <BookingEmptyImage />
                <Text color={colors.textPrimary} weight="semibold">
                  {t("crewMember.scheduledTimeOff.emptyState.title")}
                </Text>
                <Text color={colors.textSecondaryDark}>
                  {t("crewMember.scheduledTimeOff.emptyState.subtitle")}
                </Text>
              </Flex>
            </Card>
          }
        >
          <Flex vertical className={styles.tableContainer}>
            <DataTable
              columns={columns}
              dataSource={paginatedData}
              pagination={false}
              rowClickKey="id"
            />

            {/* Pagination */}
            <CustomPagination
              metadata={metadata}
              pageSize={pageSize}
              total={total}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setCurrentPage(1)
              }}
              pageSizeOptions={[5, 10, 20]}
            />
          </Flex>
        </WithEmptyStateWrapper>
      </Flex>

      {/* Schedule Time Off Modal */}
      <ScheduleTimeOffModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onSave={handleSave}
        onSaveAndAddNew={handleSaveAndAddNew}
        onDeleteTimeOff={handleDelete}
        editMode={!!editingRecord}
        initialValues={editingRecord}
        timeOffData={timeOffData}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title={t("crewMember.scheduledTimeOff.deleteConfirmation.title")}
        info={t("crewMember.scheduledTimeOff.deleteConfirmation.message")}
        onCancel={cancelDelete}
        onDelete={confirmDelete}
        deleteLabel={t("crewMember.scheduledTimeOff.table.actions.delete")}
        cancelLabel={t("crewMember.scheduledTimeOff.buttons.cancel")}
      />
    </>
  )
}

export default ScheduledTimeOff
